/**
 * Привязка ядра софтфона к React.
 *
 * Клиент и элемент со звуком создаются один раз на модуль, а не в эффекте:
 * StrictMode в деве монтирует дерево дважды, и создание клиента в компоненте
 * порвало бы RTCPeerConnection и регистрацию на ровном месте (спека §7.9).
 * Провайдер только связывает уже живой синглтон с деревом.
 */

import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { createSipJsStack, createSoftphoneClient } from '@cc/softphone-core';
import type { SoftphoneClient, SoftphoneConfig } from '@cc/softphone-core';

import { TabLeader } from './tab-leader.js';

/** Элемент со звуком станции живёт вне React-дерева и переживает любой ремаунт */
function ensureAudioElement(): HTMLAudioElement | undefined {
  if (typeof document === 'undefined') return undefined;
  const existing = document.getElementById('cc-softphone-audio');
  if (existing instanceof HTMLAudioElement) return existing;
  const element = document.createElement('audio');
  element.id = 'cc-softphone-audio';
  element.autoplay = true;
  document.body.appendChild(element);
  primeAudioOnFirstGesture(element);
  return element;
}

/**
 * Прогрев звука первым жестом (§7.11, инцидент 01.09.2026).
 *
 * Свежеоткрытая страница без единого клика не имеет права играть звук:
 * карусельный звонок, снятый авто-ответом, приходил НЕМЫМ — Екатерина не
 * слышала абонента, абонент слушал тишину. Тихий play() внутри первого же
 * жеста (клик, клавиша) выдаёт элементу разрешение навсегда — дальше
 * программные play() со звуком станции работают без ограничений.
 *
 * 100 мс тишины WAV: короче нельзя (пустой src роняет play с ошибкой),
 * длиннее незачем.
 */
const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=';

function primeAudioOnFirstGesture(element: HTMLAudioElement): void {
  const prime = (): void => {
    document.removeEventListener('pointerdown', prime);
    document.removeEventListener('keydown', prime);
    // Живой звук уже играет — прогрев не нужен и src трогать нельзя
    if (element.srcObject !== null) return;
    element.src = SILENT_WAV;
    element
      .play()
      .then(() => {
        element.removeAttribute('src');
      })
      .catch(() => {
        // Не разблокировалось — страховка в софтфоне отклонит авто-ответ,
        // немого разговора не будет
      });
  };
  document.addEventListener('pointerdown', prime);
  document.addEventListener('keydown', prime);
}

let clientSingleton: SoftphoneClient | null = null;

export function getSoftphoneClient(): SoftphoneClient {
  if (!clientSingleton) {
    const audioElement = ensureAudioElement();
    clientSingleton = createSoftphoneClient({
      createStack: createSipJsStack,
      ...(audioElement !== undefined ? { audioElement } : {}),
    });
  }
  return clientSingleton;
}

const SoftphoneContext = createContext<SoftphoneClient | null>(null);

export interface SoftphoneProviderProps {
  /** Параметры SIP приходят с сервера при входе; null — диспетчеру звонить нечем */
  config: SoftphoneConfig | null;
  children: ReactNode;
}

/**
 * Действия по починке линии для интерфейса (10.09.2026).
 *
 * Диспетчер не должен звать администратора из-за того, что линия не поднялась
 * или её держит другая вкладка: обе беды чинятся одной кнопкой на пульте.
 */
export interface LineRepair {
  /** Забрать линию в эту вкладку */
  takeLine: () => void;
  /** Поднять линию заново, не перезагружая страницу */
  reconnect: () => void;
}

const LineRepairContext = createContext<LineRepair | null>(null);

export function useLineRepair(): LineRepair {
  return (
    useContext(LineRepairContext) ?? {
      takeLine: () => undefined,
      reconnect: () => undefined,
    }
  );
}

export function SoftphoneProvider({ config, children }: SoftphoneProviderProps): React.JSX.Element {
  const client = useMemo(() => getSoftphoneClient(), []);
  const leaderRef = useRef<TabLeader | null>(null);

  useEffect(() => {
    const leader = new TabLeader({
      onChange: (isLeader) => {
        client.setLeaderTab(isLeader);
      },
    });
    leaderRef.current = leader;
    leader.start();
    return () => {
      leader.stop();
      leaderRef.current = null;
    };
  }, [client]);

  useEffect(() => {
    if (!config) return;
    void client.connect(config);
    // Отключение при размонтировании специально не делаем: провайдер живёт
    // столько же, сколько вкладка, а разрыв на ремаунте порвал бы разговор.
  }, [client, config]);

  const repair = useMemo<LineRepair>(
    () => ({
      takeLine: () => {
        leaderRef.current?.requestTakeover();
      },
      reconnect: () => {
        if (!config) return;
        // Разрыв и подъём заново: connect() на живом стеке ничего не делает
        void client.disconnect().then(() => client.connect(config));
      },
    }),
    [client, config],
  );

  return (
    <SoftphoneContext.Provider value={client}>
      <LineRepairContext.Provider value={repair}>{children}</LineRepairContext.Provider>
    </SoftphoneContext.Provider>
  );
}

export function useSoftphoneClient(): SoftphoneClient {
  const client = useContext(SoftphoneContext);
  if (!client) throw new Error('useSoftphoneClient вызван вне SoftphoneProvider');
  return client;
}
